import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import HighlightExtension from '@tiptap/extension-highlight';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import CharacterCount from '@tiptap/extension-character-count';
import TextAlign from '@tiptap/extension-text-align';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import FontFamily from '@tiptap/extension-font-family';
import { SearchAndReplace } from '@sereneinserenade/tiptap-search-and-replace';
import { FontSize } from './editor/FontSize';
import {
  Box,
  Divider,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popover,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem as SelectItem,
} from '@mui/material';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatStrikethrough from '@mui/icons-material/FormatStrikethrough';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import InsertLink from '@mui/icons-material/InsertLink';
import LinkOff from '@mui/icons-material/LinkOff';
import Title from '@mui/icons-material/Title';
import FormatColorText from '@mui/icons-material/FormatColorText';
import HighlightIcon from '@mui/icons-material/Highlight';
import Code from '@mui/icons-material/Code';
import HorizontalRule from '@mui/icons-material/HorizontalRule';
import TableChart from '@mui/icons-material/TableChart';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import Delete from '@mui/icons-material/Delete';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import CheckBox from '@mui/icons-material/CheckBox';
import ImageIcon from '@mui/icons-material/Image';
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRight from '@mui/icons-material/FormatAlignRight';
import Undo from '@mui/icons-material/Undo';
import Redo from '@mui/icons-material/Redo';
import FormatQuote from '@mui/icons-material/FormatQuote';
import FormatClear from '@mui/icons-material/FormatClear';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';

export type EditorPreset = 'lite' | 'full';

interface RichTextEditorProps {
  initialContent: string;
  onChange?: (html: string) => void;
  readonly?: boolean;
  customToolbarItems?: ReactNode[];
  /**
   * 'lite' (default) = the original bookmark-notes toolbar/extension set.
   * 'full' = adds underline, task lists, images, text-align, char count, and a
   * selection bubble menu (for the Notes app).
   */
  preset?: EditorPreset;
  /** Upload handler for images (full preset). Returns the public URL to embed. */
  onImageUpload?: (file: File) => Promise<string>;
  /**
   * When true, the editor fills its parent's height (flex column): toolbar +
   * footer stay fixed and the content region scrolls. Used by the full-body
   * note editor. Default false keeps the original auto-height behavior for
   * bookmark notes.
   */
  fillHeight?: boolean;
  /**
   * Extra items appended to the menu bar's Insert menu (full preset). Each is
   * rendered as-is (typically a MUI MenuItem). Lets callers add app-specific
   * inserts — e.g. the note editor's "Insert Template" — without the shared
   * editor depending on note logic. Receives a `closeMenu` via render prop.
   */
  insertMenuItems?: (closeMenu: () => void) => ReactNode;
  /**
   * App-specific items for the File menu (full preset). When provided, a File
   * menu is shown. Used by the note editor for Rename / Move / Delete / etc.
   */
  fileMenuItems?: (closeMenu: () => void) => ReactNode;
  /**
   * Initial in-note search term (full preset). When set/changed, the editor
   * highlights matches and scrolls to the first one, and opens the find bar.
   * Used to jump to a match from a global search result.
   */
  initialSearchTerm?: string;
  /**
   * Remote content to reconcile into the ALREADY-created editor without
   * reinitializing it (full preset / notes live-sync). When this changes to a
   * defined value that differs from the editor's current HTML, the editor's
   * document is replaced via `setContent(html, false)` (emitUpdate=false) so it
   * does NOT fire onUpdate — i.e. it cannot trip the dirty flag or trigger
   * autosave. The parent (note model) only changes this on a real cross-instance
   * reconcile, never on local typing, so there is no loop. Bookmarks (`lite`)
   * never pass this, so their behavior is byte-for-byte unchanged.
   */
  externalContent?: string;
}

/**
 * Detect if content is legacy plain text (no HTML tags).
 * If so, wrap each line in a <p> element.
 */
function normalizeContent(content: string): string {
  if (!content) return '';
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
  if (hasHtmlTags) return content;
  return content
    .split('\n')
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('');
}

const TEXT_COLORS = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#D32F2F',
  '#F57C00',
  '#388E3C',
  '#1976D2',
  '#7B1FA2',
  '#C2185B',
  '#00796B',
  '#455A64',
];

const HIGHLIGHT_COLORS = [
  '#FFEB3B',
  '#FFF9C4',
  '#C8E6C9',
  '#BBDEFB',
  '#F8BBD0',
  '#FFE0B2',
  '#E1BEE7',
  '#B2DFDB',
];

const FONT_FAMILIES: { label: string; value: string }[] = [
  { label: 'Default', value: '' },
  { label: 'Sans Serif', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Monospace', value: '"Courier New", monospace' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const FONT_SIZES = ['10', '11', '12', '14', '16', '18', '24', '32', '48'];

interface ColorPaletteProps {
  colors: string[];
  onSelect: (color: string) => void;
  onClear: () => void;
}

const ColorPalette: React.FC<ColorPaletteProps> = ({
  colors,
  onSelect,
  onClear,
}) => (
  <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 140 }}>
      {colors.map((color) => (
        <Box
          key={color}
          onClick={() => onSelect(color)}
          sx={{
            width: 24,
            height: 24,
            bgcolor: color,
            borderRadius: 0.5,
            cursor: 'pointer',
            border: 1,
            borderColor: 'divider',
            '&:hover': { transform: 'scale(1.2)' },
          }}
        />
      ))}
    </Box>
    <Button
      size="small"
      onClick={onClear}
      sx={{ textTransform: 'none', mt: 0.5 }}
    >
      Clear
    </Button>
  </Box>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  onChange,
  readonly = false,
  customToolbarItems,
  preset = 'lite',
  onImageUpload,
  fillHeight = false,
  insertMenuItems,
  fileMenuItems,
  initialSearchTerm,
  externalContent,
}) => {
  const isFull = preset === 'full';
  const [findOpen, setFindOpen] = useState(false);
  const [findTerm, setFindTerm] = useState('');
  const findInputRef = useRef<HTMLInputElement | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [textColorAnchor, setTextColorAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [highlightAnchor, setHighlightAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [menuBarAnchor, setMenuBarAnchor] = useState<{
    which: 'file' | 'edit' | 'view' | 'insert' | 'format';
    el: HTMLElement;
  } | null>(null);
  const closeMenuBar = useCallback(() => setMenuBarAnchor(null), []);
  const [showCount, setShowCount] = useState(true);
  // Custom right-click context menu (full preset): cursor position, or null.
  const [contextMenu, setContextMenu] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Keep the latest upload handler without forcing the editor to re-init.
  const onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;

  const fullExtensions: Extension[] = isFull
    ? ([
        Underline,
        TaskList,
        TaskItem.configure({ nested: true }),
        Image.configure({ inline: false, allowBase64: false }),
        CharacterCount,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Subscript,
        Superscript,
        FontFamily,
        FontSize,
        SearchAndReplace.configure({
          searchResultClass: 'search-result',
        }),
      ] as unknown as Extension[])
    : [];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bold: {},
        italic: {},
        strike: {},
        bulletList: {},
        orderedList: {},
        heading: { levels: [1, 2, 3] },
        code: {},
        codeBlock: {},
        blockquote: {},
        horizontalRule: {},
      }),
      Link.configure({
        openOnClick: readonly,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      TextStyle,
      Color,
      HighlightExtension.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      ...fullExtensions,
    ],
    content: normalizeContent(initialContent),
    editable: !readonly,
    editorProps: isFull
      ? {
          handlePaste: (_view, event) => {
            const items = event.clipboardData?.items;
            if (!items || !onImageUploadRef.current) return false;
            const images: File[] = [];
            for (let i = 0; i < items.length; i++) {
              if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) images.push(file);
              }
            }
            if (images.length > 0) {
              event.preventDefault();
              handleImageFiles(images);
              return true;
            }
            return false;
          },
          handleDrop: (_view, event) => {
            const files = (event as DragEvent).dataTransfer?.files;
            if (!files || files.length === 0 || !onImageUploadRef.current) {
              return false;
            }
            const images = Array.from(files).filter((f) =>
              f.type.startsWith('image/'),
            );
            if (images.length > 0) {
              event.preventDefault();
              handleImageFiles(images);
              return true;
            }
            return false;
          },
        }
      : undefined,
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
  });

  const handleImageFiles = useCallback(
    async (files: FileList | File[]) => {
      const upload = onImageUploadRef.current;
      if (!editor || !upload) return;
      const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
      for (const file of list) {
        try {
          const url = await upload(file);
          if (url) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        } catch {
          // Upload glue surfaces its own errors via snackbar.
        }
      }
    },
    [editor],
  );

  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleImageFiles(e.target.files);
      }
      e.target.value = '';
    },
    [handleImageFiles],
  );

  const handleOpenLinkDialog = useCallback(() => {
    if (!editor) return;
    const existingHref = editor.getAttributes('link').href || '';
    setLinkUrl(existingHref);
    setLinkDialogOpen(true);
  }, [editor]);

  const handleSetLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  const handleTextColor = useCallback(
    (color: string) => {
      if (!editor) return;
      editor.chain().focus().setColor(color).run();
      setTextColorAnchor(null);
    },
    [editor],
  );

  const handleClearTextColor = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetColor().run();
    setTextColorAnchor(null);
  }, [editor]);

  const handleHighlightColor = useCallback(
    (color: string) => {
      if (!editor) return;
      editor.chain().focus().toggleHighlight({ color }).run();
      setHighlightAnchor(null);
    },
    [editor],
  );

  const handleClearHighlight = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetHighlight().run();
    setHighlightAnchor(null);
  }, [editor]);

  const handleInsertTable = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  }, [editor]);

  // --- In-note find (full preset) ---
  // Drive the search extension whenever the find term changes.
  // Scroll the current search result into view. Reads the result range from the
  // extension storage and scrolls its DOM node into the center. Runs after the
  // search plugin has recomputed results (rAF), which a setTimeout(0) can miss.
  const scrollToCurrentResult = useCallback(() => {
    if (!editor) return;
    requestAnimationFrame(() => {
      const s = (editor.storage as any).searchAndReplace;
      const results: { from: number; to: number }[] = s?.results ?? [];
      if (results.length === 0) return;
      const idx = Math.min(s?.resultIndex ?? 0, results.length - 1);
      const range = results[idx];
      if (!range) return;
      try {
        const dom = editor.view.domAtPos(range.from)?.node as
          | HTMLElement
          | Text
          | undefined;
        const el =
          dom instanceof HTMLElement ? dom : dom?.parentElement ?? null;
        if (el && 'scrollIntoView' in el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      } catch {
        // Position may be transiently invalid; ignore.
      }
    });
  }, [editor]);

  useEffect(() => {
    if (!editor || !isFull) return;
    const chain = editor.chain() as unknown as {
      setSearchTerm: (t: string) => { run: () => void };
    };
    chain.setSearchTerm(findTerm).run();
    if (findTerm.trim()) {
      scrollToCurrentResult();
    }
  }, [editor, isFull, findTerm, scrollToCurrentResult]);

  // External term (from a global search-result click): open find + seed term.
  useEffect(() => {
    if (!isFull) return;
    if (initialSearchTerm && initialSearchTerm.trim()) {
      setFindTerm(initialSearchTerm);
      setFindOpen(true);
    }
  }, [initialSearchTerm, isFull]);

  // Reconcile remote content into the live editor (notes cross-instance sync).
  // Runs only when the parent hands a new, defined `externalContent` — which the
  // note model does ONLY on a real reconcile (not-dirty + differs + not-own-echo).
  // setContent(html, false) swaps the document in place WITHOUT emitting an
  // update, so it never fires onUpdate / trips dirty / triggers autosave, and it
  // does NOT reinitialize the editor (no note_id key change → no ProseMirror
  // removeChild teardown race). The skip-if-identical guard avoids a redundant
  // transaction.
  useEffect(() => {
    if (!editor) return;
    if (externalContent === undefined) return;
    // Skip if identical to avoid a redundant transaction.
    if (externalContent === editor.getHTML()) return;
    editor.commands.setContent(externalContent, false);
  }, [editor, externalContent]);

  const findGo = useCallback(
    (dir: 'next' | 'prev') => {
      if (!editor) return;
      const c = editor.chain() as unknown as {
        nextSearchResult: () => { run: () => void };
        previousSearchResult: () => { run: () => void };
      };
      if (dir === 'next') c.nextSearchResult().run();
      else c.previousSearchResult().run();
      scrollToCurrentResult();
    },
    [editor, scrollToCurrentResult],
  );

  const closeFind = useCallback(() => {
    setFindOpen(false);
    setFindTerm('');
  }, []);

  const searchStorage = (editor?.storage as any)?.searchAndReplace;
  const findTotal: number = searchStorage?.results?.length ?? 0;
  const findCurrent: number =
    findTotal > 0 ? (searchStorage?.resultIndex ?? 0) + 1 : 0;

  // Menu-bar / richer-toolbar action helpers (full preset).
  const runCmd = useCallback(
    (fn: () => void) => {
      fn();
      closeMenuBar();
    },
    [closeMenuBar],
  );

  const handleClearFormatting = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetAllMarks().clearNodes().run();
  }, [editor]);

  const handleSetHeading = useCallback(
    (level: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
      if (!editor) return;
      if (level === 0) {
        editor.chain().focus().setParagraph().run();
      } else {
        editor.chain().focus().toggleHeading({ level }).run();
      }
    },
    [editor],
  );

  const currentBlock: string = editor
    ? editor.isActive('heading', { level: 1 })
      ? 'h1'
      : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
      ? 'h3'
      : editor.isActive('heading', { level: 4 })
      ? 'h4'
      : editor.isActive('heading', { level: 5 })
      ? 'h5'
      : editor.isActive('heading', { level: 6 })
      ? 'h6'
      : 'p'
    : 'p';

  const currentFontFamily: string = editor
    ? editor.getAttributes('textStyle').fontFamily || ''
    : '';
  const currentFontSize: string = editor
    ? (editor.getAttributes('textStyle').fontSize || '').replace('px', '')
    : '';

  if (!editor) return null;

  return (
    <Box
      onKeyDown={
        isFull
          ? (e) => {
              if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                setFindOpen(true);
                window.setTimeout(() => findInputRef.current?.focus(), 0);
              } else if (e.key === 'Escape' && findOpen) {
                closeFind();
              }
            }
          : undefined
      }
      sx={{
        position: 'relative',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        ...(fillHeight && {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }),
        '& .search-result': {
          backgroundColor: 'rgba(255, 213, 79, 0.4)',
        },
        '& .search-result-current': {
          backgroundColor: 'rgba(255, 145, 0, 0.6)',
        },
      }}
    >
      {/* In-note find bar (full preset) */}
      {isFull && findOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          <TextField
            inputRef={findInputRef}
            size="small"
            variant="standard"
            placeholder="Find in note"
            value={findTerm}
            onChange={(e) => setFindTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                findGo(e.shiftKey ? 'prev' : 'next');
              }
            }}
            sx={{ width: 160 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 44, textAlign: 'center' }}
          >
            {findCurrent}/{findTotal}
          </Typography>
          <IconButton
            size="small"
            onClick={() => findGo('prev')}
            disabled={findTotal === 0}
            aria-label="Previous match"
          >
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => findGo('next')}
            disabled={findTotal === 0}
            aria-label="Next match"
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={closeFind} aria-label="Close find">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {/* Menu bar (full preset): Word/Docs-style Edit / Insert / Format menus */}
      {isFull && !readonly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            ...(fillHeight && { flexShrink: 0 }),
          }}
        >
          {(
            [
              ...(fileMenuItems ? (['file'] as const) : []),
              'edit',
              'view',
              'insert',
              'format',
            ] as const
          ).map((which) => (
            <Button
              key={which}
              size="small"
              onClick={(e) => setMenuBarAnchor({ which, el: e.currentTarget })}
              sx={{
                textTransform: 'capitalize',
                color: 'text.primary',
                minWidth: 0,
                px: 1,
              }}
            >
              {which}
            </Button>
          ))}

          {/* File menu (app-injected) */}
          {fileMenuItems && (
            <Menu
              anchorEl={menuBarAnchor?.el}
              open={menuBarAnchor?.which === 'file'}
              onClose={closeMenuBar}
            >
              {fileMenuItems(closeMenuBar)}
            </Menu>
          )}

          {/* Edit menu */}
          <Menu
            anchorEl={menuBarAnchor?.el}
            open={menuBarAnchor?.which === 'edit'}
            onClose={closeMenuBar}
          >
            <MenuItem
              onClick={() => runCmd(() => editor.chain().focus().undo().run())}
              disabled={!editor.can().undo()}
            >
              <ListItemIcon>
                <Undo fontSize="small" />
              </ListItemIcon>
              <ListItemText>Undo</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => runCmd(() => editor.chain().focus().redo().run())}
              disabled={!editor.can().redo()}
            >
              <ListItemIcon>
                <Redo fontSize="small" />
              </ListItemIcon>
              <ListItemText>Redo</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().selectAll().run())
              }
            >
              <ListItemText inset>Select all</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => {
                  setFindOpen(true);
                  window.setTimeout(() => findInputRef.current?.focus(), 0);
                })
              }
            >
              <ListItemText inset>Find… (Ctrl+F)</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => runCmd(handleClearFormatting)}>
              <ListItemIcon>
                <FormatClear fontSize="small" />
              </ListItemIcon>
              <ListItemText>Clear formatting</ListItemText>
            </MenuItem>
          </Menu>

          {/* View menu */}
          <Menu
            anchorEl={menuBarAnchor?.el}
            open={menuBarAnchor?.which === 'view'}
            onClose={closeMenuBar}
          >
            <MenuItem onClick={() => runCmd(() => setShowCount((v) => !v))}>
              <ListItemText>
                {showCount ? 'Hide word count' : 'Show word count'}
              </ListItemText>
            </MenuItem>
          </Menu>

          {/* Insert menu */}
          <Menu
            anchorEl={menuBarAnchor?.el}
            open={menuBarAnchor?.which === 'insert'}
            onClose={closeMenuBar}
          >
            {onImageUpload && (
              <MenuItem onClick={() => runCmd(handleImageButtonClick)}>
                <ListItemIcon>
                  <ImageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Image</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => runCmd(handleInsertTable)}>
              <ListItemIcon>
                <TableChart fontSize="small" />
              </ListItemIcon>
              <ListItemText>Table</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenuBar();
                handleOpenLinkDialog();
              }}
            >
              <ListItemIcon>
                <InsertLink fontSize="small" />
              </ListItemIcon>
              <ListItemText>Link</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleTaskList().run())
              }
            >
              <ListItemIcon>
                <CheckBox fontSize="small" />
              </ListItemIcon>
              <ListItemText>Checklist</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleCodeBlock().run())
              }
            >
              <ListItemIcon>
                <Code fontSize="small" />
              </ListItemIcon>
              <ListItemText>Code block</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().setHorizontalRule().run())
              }
            >
              <ListItemIcon>
                <HorizontalRule fontSize="small" />
              </ListItemIcon>
              <ListItemText>Horizontal rule</ListItemText>
            </MenuItem>
            {/* App-injected inserts (e.g. Insert Template) */}
            {insertMenuItems && insertMenuItems(closeMenuBar)}
          </Menu>

          {/* Format menu */}
          <Menu
            anchorEl={menuBarAnchor?.el}
            open={menuBarAnchor?.which === 'format'}
            onClose={closeMenuBar}
          >
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleBold().run())
              }
            >
              <ListItemIcon>
                <FormatBold fontSize="small" />
              </ListItemIcon>
              <ListItemText>Bold</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleItalic().run())
              }
            >
              <ListItemIcon>
                <FormatItalic fontSize="small" />
              </ListItemIcon>
              <ListItemText>Italic</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleUnderline().run())
              }
            >
              <ListItemIcon>
                <FormatUnderlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Underline</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleStrike().run())
              }
            >
              <ListItemIcon>
                <FormatStrikethrough fontSize="small" />
              </ListItemIcon>
              <ListItemText>Strikethrough</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleSubscript().run())
              }
            >
              <ListItemIcon>
                <SubscriptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Subscript</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleSuperscript().run())
              }
            >
              <ListItemIcon>
                <SuperscriptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Superscript</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleBlockquote().run())
              }
            >
              <ListItemIcon>
                <FormatQuote fontSize="small" />
              </ListItemIcon>
              <ListItemText>Blockquote</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleBulletList().run())
              }
            >
              <ListItemIcon>
                <FormatListBulleted fontSize="small" />
              </ListItemIcon>
              <ListItemText>Bullet list</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                runCmd(() => editor.chain().focus().toggleOrderedList().run())
              }
            >
              <ListItemIcon>
                <FormatListNumbered fontSize="small" />
              </ListItemIcon>
              <ListItemText>Numbered list</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => runCmd(handleClearFormatting)}>
              <ListItemIcon>
                <FormatClear fontSize="small" />
              </ListItemIcon>
              <ListItemText>Clear formatting</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      )}

      {(!readonly || (customToolbarItems && customToolbarItems.length > 0)) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.25,
            px: 0.5,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            ...(fillHeight && { flexShrink: 0 }),
          }}
        >
          {!readonly && (
            <>
              {isFull && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    aria-label="Undo"
                  >
                    <Undo fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    aria-label="Redo"
                  >
                    <Redo fontSize="small" />
                  </IconButton>
                  <Select
                    size="small"
                    value={currentBlock}
                    onChange={(e) => {
                      const v = e.target.value;
                      const level =
                        v === 'p' ? 0 : (Number(v[1]) as 1 | 2 | 3 | 4 | 5 | 6);
                      handleSetHeading(level as 0 | 1 | 2 | 3 | 4 | 5 | 6);
                    }}
                    sx={{
                      minWidth: 96,
                      mx: 0.5,
                      '& .MuiSelect-select': { py: 0.25, fontSize: '0.8rem' },
                    }}
                  >
                    <SelectItem value="p">Paragraph</SelectItem>
                    <SelectItem value="h1">Heading 1</SelectItem>
                    <SelectItem value="h2">Heading 2</SelectItem>
                    <SelectItem value="h3">Heading 3</SelectItem>
                    <SelectItem value="h4">Heading 4</SelectItem>
                    <SelectItem value="h5">Heading 5</SelectItem>
                    <SelectItem value="h6">Heading 6</SelectItem>
                  </Select>
                  <Select
                    size="small"
                    value={currentFontFamily}
                    displayEmpty
                    onChange={(e) => {
                      const v = e.target.value as string;
                      if (v) {
                        editor.chain().focus().setFontFamily(v).run();
                      } else {
                        editor.chain().focus().unsetFontFamily().run();
                      }
                    }}
                    sx={{
                      minWidth: 110,
                      mr: 0.5,
                      '& .MuiSelect-select': { py: 0.25, fontSize: '0.8rem' },
                    }}
                  >
                    {FONT_FAMILIES.map((f) => (
                      <SelectItem key={f.label} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    size="small"
                    value={currentFontSize}
                    displayEmpty
                    onChange={(e) => {
                      const v = e.target.value as string;
                      if (v) {
                        editor.chain().focus().setFontSize(`${v}px`).run();
                      } else {
                        editor.chain().focus().unsetFontSize().run();
                      }
                    }}
                    sx={{
                      minWidth: 64,
                      mr: 0.5,
                      '& .MuiSelect-select': { py: 0.25, fontSize: '0.8rem' },
                    }}
                  >
                    <SelectItem value="">--</SelectItem>
                    {FONT_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </Select>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                </>
              )}
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBold().run()}
                color={editor.isActive('bold') ? 'primary' : 'default'}
                aria-label="Bold"
              >
                <FormatBold fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                color={editor.isActive('italic') ? 'primary' : 'default'}
                aria-label="Italic"
              >
                <FormatItalic fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                color={editor.isActive('strike') ? 'primary' : 'default'}
                aria-label="Strikethrough"
              >
                <FormatStrikethrough fontSize="small" />
              </IconButton>
              {isFull && (
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  color={editor.isActive('underline') ? 'primary' : 'default'}
                  aria-label="Underline"
                >
                  <FormatUnderlined fontSize="small" />
                </IconButton>
              )}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              {/* Text Color */}
              <IconButton
                size="small"
                onClick={(e) => setTextColorAnchor(e.currentTarget)}
                aria-label="Text Color"
              >
                <FormatColorText fontSize="small" />
              </IconButton>
              <Popover
                open={Boolean(textColorAnchor)}
                anchorEl={textColorAnchor}
                onClose={() => setTextColorAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <ColorPalette
                  colors={TEXT_COLORS}
                  onSelect={handleTextColor}
                  onClear={handleClearTextColor}
                />
              </Popover>

              {/* Highlight Color */}
              <IconButton
                size="small"
                onClick={(e) => setHighlightAnchor(e.currentTarget)}
                color={editor.isActive('highlight') ? 'primary' : 'default'}
                aria-label="Highlight Color"
              >
                <HighlightIcon fontSize="small" />
              </IconButton>
              <Popover
                open={Boolean(highlightAnchor)}
                anchorEl={highlightAnchor}
                onClose={() => setHighlightAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <ColorPalette
                  colors={HIGHLIGHT_COLORS}
                  onSelect={handleHighlightColor}
                  onClear={handleClearHighlight}
                />
              </Popover>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                color={editor.isActive('bulletList') ? 'primary' : 'default'}
                aria-label="Bullet List"
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                color={editor.isActive('orderedList') ? 'primary' : 'default'}
                aria-label="Ordered List"
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
              {isFull && (
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleTaskList().run()}
                  color={editor.isActive('taskList') ? 'primary' : 'default'}
                  aria-label="Checklist"
                >
                  <CheckBox fontSize="small" />
                </IconButton>
              )}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <IconButton
                size="small"
                onClick={handleOpenLinkDialog}
                color={editor.isActive('link') ? 'primary' : 'default'}
                aria-label="Add Link"
              >
                <InsertLink fontSize="small" />
              </IconButton>
              {editor.isActive('link') && (
                <IconButton
                  size="small"
                  onClick={handleRemoveLink}
                  aria-label="Remove Link"
                >
                  <LinkOff fontSize="small" />
                </IconButton>
              )}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                aria-label="Code Block"
              >
                <Code fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                aria-label="Horizontal Rule"
              >
                <HorizontalRule fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleInsertTable}
                color={editor.isActive('table') ? 'primary' : 'default'}
                aria-label="Insert Table"
              >
                <TableChart fontSize="small" />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 1 }).run()
                }
                color={
                  editor.isActive('heading', { level: 1 })
                    ? 'primary'
                    : 'default'
                }
                aria-label="Heading 1"
                sx={{ fontSize: '0.75rem', fontWeight: 700 }}
              >
                <Title fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                }
                color={
                  editor.isActive('heading', { level: 2 })
                    ? 'primary'
                    : 'default'
                }
                aria-label="Heading 2"
                sx={{ fontSize: '0.65rem', fontWeight: 600 }}
              >
                <Title fontSize="small" sx={{ transform: 'scale(0.85)' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 3 }).run()
                }
                color={
                  editor.isActive('heading', { level: 3 })
                    ? 'primary'
                    : 'default'
                }
                aria-label="Heading 3"
                sx={{ fontSize: '0.6rem', fontWeight: 500 }}
              >
                <Title fontSize="small" sx={{ transform: 'scale(0.7)' }} />
              </IconButton>
              {isFull && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <IconButton
                    size="small"
                    onClick={() =>
                      editor.chain().focus().setTextAlign('left').run()
                    }
                    color={
                      editor.isActive({ textAlign: 'left' })
                        ? 'primary'
                        : 'default'
                    }
                    aria-label="Align left"
                  >
                    <FormatAlignLeft fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      editor.chain().focus().setTextAlign('center').run()
                    }
                    color={
                      editor.isActive({ textAlign: 'center' })
                        ? 'primary'
                        : 'default'
                    }
                    aria-label="Align center"
                  >
                    <FormatAlignCenter fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      editor.chain().focus().setTextAlign('right').run()
                    }
                    color={
                      editor.isActive({ textAlign: 'right' })
                        ? 'primary'
                        : 'default'
                    }
                    aria-label="Align right"
                  >
                    <FormatAlignRight fontSize="small" />
                  </IconButton>
                  {onImageUpload && (
                    <>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 0.5 }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleImageButtonClick}
                        aria-label="Insert image"
                      >
                        <ImageIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <IconButton
                    size="small"
                    onClick={() =>
                      editor.chain().focus().toggleBlockquote().run()
                    }
                    color={
                      editor.isActive('blockquote') ? 'primary' : 'default'
                    }
                    aria-label="Blockquote"
                  >
                    <FormatQuote fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      editor.chain().focus().toggleSubscript().run()
                    }
                    color={editor.isActive('subscript') ? 'primary' : 'default'}
                    aria-label="Subscript"
                  >
                    <SubscriptIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      editor.chain().focus().toggleSuperscript().run()
                    }
                    color={
                      editor.isActive('superscript') ? 'primary' : 'default'
                    }
                    aria-label="Superscript"
                  >
                    <SuperscriptIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleClearFormatting}
                    aria-label="Clear formatting"
                  >
                    <FormatClear fontSize="small" />
                  </IconButton>
                </>
              )}
            </>
          )}
          {customToolbarItems &&
            customToolbarItems.map((item, index) => (
              <React.Fragment key={index}>{item}</React.Fragment>
            ))}
        </Box>
      )}

      {/* Table context toolbar: visible when cursor is inside a table */}
      {!readonly && editor.isActive('table') && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.selected',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Table:
          </Typography>
          <Tooltip title="Add column after">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              aria-label="Add column after"
            >
              <AddCircleOutline
                fontSize="small"
                sx={{ transform: 'rotate(0deg)' }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove column">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              aria-label="Remove column"
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Add row after">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              aria-label="Add row after"
            >
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove row">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().deleteRow().run()}
              aria-label="Remove row"
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Toggle header row">
            <Button
              size="small"
              variant={
                editor.isActive('tableHeader') ? 'contained' : 'outlined'
              }
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                fontSize: '0.7rem',
              }}
            >
              Header
            </Button>
          </Tooltip>
          <Tooltip title="Merge/split cells">
            <Button
              size="small"
              variant="outlined"
              onClick={() => editor.chain().focus().mergeOrSplit().run()}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                fontSize: '0.7rem',
              }}
            >
              Merge/Split
            </Button>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Delete table">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().deleteTable().run()}
              color="error"
              aria-label="Delete table"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Editor content area */}
      <Box
        onContextMenu={
          isFull && !readonly
            ? (e) => {
                e.preventDefault();
                setContextMenu({ top: e.clientY, left: e.clientX });
              }
            : undefined
        }
        sx={{
          px: 1.5,
          py: 1,
          minHeight: 120,
          ...(fillHeight && {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }),
          '& .ProseMirror': {
            outline: 'none',
            minHeight: fillHeight ? '100%' : 100,
            typography: 'body2',
            '& p': { my: 0.5 },
            '& h1': { typography: 'h5', mt: 1.5, mb: 0.5 },
            '& h2': { typography: 'h6', mt: 1.5, mb: 0.5 },
            '& h3': { typography: 'subtitle1', mt: 1, mb: 0.5 },
            '& ul, & ol': { pl: 3, my: 0.5 },
            '& li': { my: 0.25 },
            '& blockquote': {
              borderLeft: 3,
              borderColor: 'divider',
              pl: 1.5,
              ml: 0,
              color: 'text.secondary',
              fontStyle: 'italic',
            },
            '& code': {
              bgcolor: 'action.selected',
              borderRadius: 0.5,
              px: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.85em',
            },
            '& pre': {
              bgcolor: 'grey.900',
              color: 'grey.100',
              borderRadius: 1,
              p: 1.5,
              fontFamily: 'monospace',
              fontSize: '0.85em',
              overflow: 'auto',
              '& code': {
                bgcolor: 'transparent',
                color: 'inherit',
                p: 0,
              },
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
            '& hr': {
              border: 'none',
              borderTop: 1,
              borderColor: 'divider',
              my: 1,
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              my: 1,
            },
            '& th, & td': {
              border: 1,
              borderColor: 'divider',
              p: 0.75,
              minWidth: 50,
            },
            '& th': {
              bgcolor: 'action.hover',
              fontWeight: 600,
            },
            '& .selectedCell': {
              bgcolor: 'action.selected',
            },
            // Full-preset content: images + task lists
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 0.5,
              display: 'block',
              my: 1,
            },
            '& ul[data-type="taskList"]': {
              listStyle: 'none',
              pl: 0,
              '& li': {
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                my: 0.25,
                '& > label': {
                  flexShrink: 0,
                  mt: '0.15em',
                },
                '& > div': {
                  flex: 1,
                  '& > p': { my: 0 },
                },
              },
              '& input[type="checkbox"]': {
                cursor: 'pointer',
              },
            },
          },
        }}
      >
        {isFull && editor && (
          <BubbleMenu
            editor={editor}
            tippyOptions={{ duration: 100 }}
            shouldShow={({ editor: ed, from, to }) =>
              !ed.isActive('image') && from !== to
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.5,
                py: 0.25,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                boxShadow: 3,
              }}
            >
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBold().run()}
                color={editor.isActive('bold') ? 'primary' : 'default'}
                aria-label="Bold"
              >
                <FormatBold fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                color={editor.isActive('italic') ? 'primary' : 'default'}
                aria-label="Italic"
              >
                <FormatItalic fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                color={editor.isActive('underline') ? 'primary' : 'default'}
                aria-label="Underline"
              >
                <FormatUnderlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleOpenLinkDialog}
                color={editor.isActive('link') ? 'primary' : 'default'}
                aria-label="Add Link"
              >
                <InsertLink fontSize="small" />
              </IconButton>
            </Box>
          </BubbleMenu>
        )}
        <EditorContent editor={editor} />
      </Box>

      {/* Custom right-click context menu (full preset) */}
      {isFull && !readonly && (
        <Menu
          open={contextMenu !== null}
          onClose={closeContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu
              ? { top: contextMenu.top, left: contextMenu.left }
              : undefined
          }
        >
          <MenuItem
            onClick={() => {
              closeContextMenu();
              document.execCommand('cut');
            }}
          >
            <ListItemText>Cut</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeContextMenu();
              document.execCommand('copy');
            }}
          >
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={async () => {
              closeContextMenu();
              try {
                const text = await navigator.clipboard.readText();
                if (text) editor.chain().focus().insertContent(text).run();
              } catch {
                // Clipboard read may be blocked; user can Ctrl+V instead.
              }
            }}
          >
            <ListItemText>Paste</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              closeContextMenu();
              editor.chain().focus().toggleBold().run();
            }}
          >
            <ListItemIcon>
              <FormatBold fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bold</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeContextMenu();
              editor.chain().focus().toggleItalic().run();
            }}
          >
            <ListItemIcon>
              <FormatItalic fontSize="small" />
            </ListItemIcon>
            <ListItemText>Italic</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeContextMenu();
              editor.chain().focus().toggleUnderline().run();
            }}
          >
            <ListItemIcon>
              <FormatUnderlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Underline</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeContextMenu();
              handleOpenLinkDialog();
            }}
          >
            <ListItemIcon>
              <InsertLink fontSize="small" />
            </ListItemIcon>
            <ListItemText>Link…</ListItemText>
          </MenuItem>
          {editor.isActive('table') && <Divider />}
          {editor.isActive('table') && (
            <MenuItem
              onClick={() => {
                closeContextMenu();
                editor.chain().focus().addRowAfter().run();
              }}
            >
              <ListItemText inset>Insert row below</ListItemText>
            </MenuItem>
          )}
          {editor.isActive('table') && (
            <MenuItem
              onClick={() => {
                closeContextMenu();
                editor.chain().focus().addColumnAfter().run();
              }}
            >
              <ListItemText inset>Insert column after</ListItemText>
            </MenuItem>
          )}
          {editor.isActive('table') && (
            <MenuItem
              onClick={() => {
                closeContextMenu();
                editor.chain().focus().deleteRow().run();
              }}
            >
              <ListItemText inset>Delete row</ListItemText>
            </MenuItem>
          )}
          {editor.isActive('table') && (
            <MenuItem
              onClick={() => {
                closeContextMenu();
                editor.chain().focus().deleteColumn().run();
              }}
            >
              <ListItemText inset>Delete column</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}

      {/* Character/word count footer (full preset) */}
      {isFull && showCount && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            px: 1.5,
            py: 0.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            ...(fillHeight && { flexShrink: 0 }),
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {editor.storage.characterCount?.words?.() ?? 0} words
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {editor.storage.characterCount?.characters?.() ?? 0} characters
          </Typography>
        </Box>
      )}

      {/* Hidden file input for image upload (full preset) */}
      {isFull && onImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleImageInputChange}
        />
      )}

      {/* Link dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="URL"
            type="url"
            fullWidth
            variant="outlined"
            size="small"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSetLink();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSetLink} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RichTextEditor;
